import { useMemo, useState, type FormEvent } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Banner, Button, Input, Switch, Text, useKumoToastManager } from '@cloudflare/kumo'
import type {
  BuildingHours,
  GuestInvitationCreateResult,
  RecentGuest,
  RoomBookingDto,
} from '@hub-wifi/shared'
import { createGuestInvitation } from '../../api/guestInvitations'
import { useTranslation } from '../../i18n'
import { addDaysInTz, todayYmdInTz } from '../../lib/calendar-time'
import { formatWeekdayDate } from '../../lib/format'
import {
  bookingWindow,
  checkVisitWindow,
  describeGuestInvitationError,
  EMAIL_RE,
  formatStoredPhoneForDisplay,
  maskGuestPhoneInput,
  normalizeGuestPhone,
} from '../../lib/guest-invitation-form'
import { dayKindOf } from '../home/agenda'
import { startSlots } from '../home/book-sheet'
import { InvitationReady } from './InvitationReady'
import { MAX_FORM_CHIPS, RecentGuestChips } from './RecentGuestChips'
import { useNow } from './useNow'
import { PICK_DAY, WhenSection, type When } from './WhenSection'
import { defaultUntil, quickDays, untilSlots } from './visit-schedule'
import './guests.css'

const MAX_DAYS_AHEAD = 180

export interface GuestInvitationFormProps {
  timeZone: string
  hours: BuildingHours[]
  /** Booking the guest can be linked to ("With my booking"); `null` = none. */
  booking: RoomBookingDto | null
  /** All upcoming bookings the user could link instead (picker when > 1). */
  bookingChoices?: RoomBookingDto[]
  onBookingChange?: (id: string) => void
  /** The `?roomBookingId=` booking couldn't be loaded: sending is blocked. */
  bookingError?: string | null
  recentGuests: RecentGuest[]
  /** Pre-fill from `?guest=` (already matched against the recent guests). */
  initialGuest?: RecentGuest | null
  onCreated?: (invitation: GuestInvitationCreateResult) => void
}

interface GuestFields {
  name: string
  email: string
  phone: string
}

function fieldsFor(guest: RecentGuest | null | undefined): GuestFields {
  return guest
    ? {
        name: guest.guestName,
        email: guest.guestEmail,
        phone: formatStoredPhoneForDisplay(guest.guestPhone),
      }
    : { name: '', email: '', phone: '' }
}

/** The whole "New invitation" form; swaps itself for the success state once sent. */
export function GuestInvitationForm({
  timeZone,
  hours,
  booking,
  bookingChoices = [],
  onBookingChange,
  bookingError = null,
  recentGuests,
  initialGuest,
  onCreated,
}: GuestInvitationFormProps) {
  const { t, locale } = useTranslation()
  const { getAccessTokenSilently } = useAuth0()
  const toasts = useKumoToastManager()
  const now = useNow()

  const [fields, setFields] = useState<GuestFields>(() => fieldsFor(initialGuest))
  const [errors, setErrors] = useState<{ name?: string; email?: string; phone?: string }>({})
  const [singleAccess, setSingleAccess] = useState(false)
  const [when, setWhen] = useState<When>(booking ? 'booking' : 'other')
  const [dayKey, setDayKey] = useState('')
  const today = todayYmdInTz(timeZone, now)
  const [pickedYmd, setPickedYmd] = useState(() => addDaysInTz(today, 1, timeZone))
  const [fromHm, setFromHm] = useState('')
  const [untilHm, setUntilHm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [created, setCreated] = useState<GuestInvitationCreateResult | null>(null)

  const withBooking = when === 'booking' && booking !== null
  const roomName = booking?.roomName ?? t('dashboard.nextUp.room')

  // Another day: quick day tabs + "Pick a date", then From/Until on a 15-min
  // grid within building hours. Values are kept valid as the options change.
  const days = useMemo(() => quickDays(hours, timeZone, now), [hours, timeZone, now])
  const activeKey = dayKey === PICK_DAY || days.includes(dayKey) ? dayKey : (days[0] ?? PICK_DAY)
  const visitDate = activeKey === PICK_DAY ? pickedYmd : activeKey
  const fromSlots = useMemo(
    () => (visitDate ? startSlots(hours, visitDate, timeZone, now) : []),
    [hours, visitDate, timeZone, now],
  )
  const fromValue = fromSlots.includes(fromHm) ? fromHm : (fromSlots[0] ?? '')
  const untilOptions = useMemo(
    () => untilSlots(hours, visitDate, fromValue),
    [hours, visitDate, fromValue],
  )
  const untilValue = untilOptions.includes(untilHm)
    ? untilHm
    : defaultUntil(fromValue, untilOptions)
  const closed = visitDate !== '' && fromSlots.length === 0

  const visit = { visitDate, visitTimeBegin: fromValue, visitTimeEnd: untilValue }
  const visitCheck = withBooking ? null : checkVisitWindow(visit, timeZone, now)
  // Nothing to say while the day is closed: the "closed" banner covers it.
  const timeErrorKey = closed ? null : (visitCheck?.errorKey ?? null)
  const blocked = Boolean(bookingError) || (!withBooking && (closed || Boolean(timeErrorKey)))

  const labelOf = (b: RoomBookingDto) => {
    const w = bookingWindow(b, timeZone)
    return `${formatWeekdayDate(w.visitDate, locale ?? 'pt-BR')} · ${w.visitTimeBegin} – ${w.visitTimeEnd}`
  }
  const bookingLabel = booking ? labelOf(booking) : ''

  function pickRecent(guest: RecentGuest) {
    setFields(fieldsFor(guest))
    setErrors({})
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault()
    const next: typeof errors = {}
    if (fields.name.trim() === '') next.name = t('dashboard.inviteSheet.nameRequired')
    const email = fields.email.trim()
    if (email !== '' && !EMAIL_RE.test(email)) next.email = t('dashboard.inviteSheet.invalidEmail')
    // Normalise to E.164 so the API never sees the display format.
    const phone = normalizeGuestPhone(fields.phone)
    if (!phone.ok) next.phone = t('enroll.validation.invalidMobile')
    setErrors(next)
    if (Object.keys(next).length > 0 || !phone.ok || blocked) return

    const visitFields = withBooking && booking ? bookingWindow(booking, timeZone) : visit
    setSubmitting(true)
    try {
      const token = await getAccessTokenSilently()
      const dto = await createGuestInvitation(
        {
          guestName: fields.name.trim(),
          guestEmail: email || null,
          guestPhone: phone.e164,
          ...visitFields,
          singleAccess,
          language: locale,
          ...(withBooking && booking ? { roomBookingId: booking.id } : {}),
        },
        token,
      )
      setCreated(dto)
      onCreated?.(dto)
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: describeGuestInvitationError(err, t),
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (created) {
    return (
      <InvitationReady
        invitation={created}
        room={created.roomBookingId ? roomName : null}
        onInviteAnother={() => {
          setCreated(null)
          setFields(fieldsFor(null))
          setErrors({})
          setSingleAccess(false)
        }}
      />
    )
  }

  return (
    <form className="guest-form" onSubmit={(e) => void onSubmit(e)} noValidate>
      {recentGuests.length > 0 && (
        <section className="guest-form__section" aria-labelledby="guest-form-again">
          <h2 id="guest-form-again" className="guest-form__legend">
            {t('dashboard.inviteAgain.title')}
          </h2>
          <RecentGuestChips
            guests={recentGuests.slice(0, MAX_FORM_CHIPS)}
            selectedEmail={fields.email}
            onPick={pickRecent}
          />
        </section>
      )}

      {bookingError && <Banner variant="error">{bookingError}</Banner>}

      <WhenSection
        when={withBooking ? 'booking' : 'other'}
        onWhenChange={setWhen}
        booking={booking ? { room: roomName, label: bookingLabel } : null}
        bookingOptions={bookingChoices.map((b) => ({
          id: b.id,
          label: `${b.roomName ?? t('dashboard.nextUp.room')} · ${labelOf(b)}`,
        }))}
        bookingId={booking?.id}
        onBookingIdChange={onBookingChange}
        days={days.map((ymd) => ({ ymd, kind: dayKindOf(ymd, timeZone, now) }))}
        dayKey={activeKey}
        onDayKeyChange={setDayKey}
        pickedYmd={pickedYmd}
        onPickedYmdChange={setPickedYmd}
        minYmd={today}
        maxYmd={addDaysInTz(today, MAX_DAYS_AHEAD, timeZone)}
        fromSlots={fromSlots}
        fromValue={fromValue}
        onFromChange={setFromHm}
        untilOptions={untilOptions}
        untilValue={untilValue}
        onUntilChange={setUntilHm}
        closed={closed}
        errorKey={timeErrorKey}
      />

      <Input
        label={t('guestInvitations.form.guestName')}
        className="guest-control"
        autoComplete="off"
        value={fields.name}
        error={errors.name}
        aria-invalid={errors.name ? true : undefined}
        onChange={(e) => setFields((f) => ({ ...f, name: e.target.value }))}
      />
      <Input
        label={t('guestInvitations.form.guestEmail')}
        className="guest-control"
        type="email"
        inputMode="email"
        autoComplete="off"
        value={fields.email}
        error={errors.email}
        aria-invalid={errors.email ? true : undefined}
        onChange={(e) => setFields((f) => ({ ...f, email: e.target.value }))}
      />
      <Input
        label={t('guestInvitations.form.guestPhone')}
        className="guest-control"
        type="tel"
        inputMode="tel"
        placeholder="+55 11 99999-0000"
        value={fields.phone}
        error={errors.phone}
        aria-invalid={errors.phone ? true : undefined}
        onChange={(e) => {
          const raw = e.target.value
          setFields((f) => ({ ...f, phone: maskGuestPhoneInput(f.phone, raw) }))
          if (errors.phone) setErrors((x) => ({ ...x, phone: undefined }))
        }}
      />
      <Text as="p" variant="secondary">
        {t('dashboard.inviteSheet.contactHint')}
      </Text>

      <div className="flex flex-col gap-1">
        <Switch
          label={t('guestInvitations.form.singleAccess')}
          checked={singleAccess}
          onCheckedChange={setSingleAccess}
        />
        <Text as="p" variant="secondary">
          {t('guestInvitations.form.singleAccessHint')}
        </Text>
      </div>

      <div className="guest-form__bar">
        <Button
          type="submit"
          variant="primary"
          className="guest-submit"
          loading={submitting}
          disabled={submitting || blocked}
        >
          {submitting ? t('guestInvitations.form.submitting') : t('dashboard.inviteSheet.send')}
        </Button>
      </div>
    </form>
  )
}
